/**
 * SEO Schema Utilities for Go Ops
 * Generates JSON-LD structured data for GEO (Generative Engine Optimization)
 */

const BASE_URL = 'https://www.go-ops.digital';
const LOGO_URL = `${BASE_URL}/goopslogo.jpeg`;

/**
 * Organization Schema
 * https://schema.org/Organization
 */
export const getOrganizationSchema = () => ({
  '@context': 'https://schema.org',
  '@type': 'Organization',
  name: 'Go Ops',
  url: BASE_URL,
  logo: LOGO_URL,
  description: 'AI-powered garage management software for UK independent garages',
  foundingDate: '2024',
  address: {
    '@type': 'PostalAddress',
    addressCountry: 'GB'
  },
  contactPoint: {
    '@type': 'ContactPoint',
    contactType: 'sales',
    telephone: '+44-333-2101-500',
    email: 'hello@go-ops.digital',
    url: `${BASE_URL}/contact`
  },
  sameAs: [
    'https://www.linkedin.com/company/go-ops-digital/'
  ]
});

/**
 * SoftwareApplication Schema
 * https://schema.org/SoftwareApplication
 */
export const getSoftwareApplicationSchema = () => ({
  '@context': 'https://schema.org',
  '@type': 'SoftwareApplication',
  name: 'Go Ops',
  applicationCategory: 'BusinessApplication',
  applicationSubCategory: 'Garage Management Software',
  operatingSystem: 'Web, iOS, Android',
  offers: {
    '@type': 'Offer',
    price: '0',
    priceCurrency: 'GBP',
    url: `${BASE_URL}/pricing/`,
    description: 'Contact for custom pricing'
  },
  description: 'AI-powered garage management system for UK independent garages. Automates booking intake, job tracking, vehicle health checks, payments, and invoicing.',
  featureList: [
    'AI-powered booking intake from emails',
    'Visual job board for technician management',
    'Digital vehicle health checks with photos and videos',
    'Secure customer payment links',
    'Automatic invoice generation',
    'Mobile-friendly interface'
  ],
  screenshot: LOGO_URL,
  softwareVersion: '1.0',
  author: {
    '@type': 'Organization',
    name: 'Go Ops'
  }
});

/**
 * FAQPage Schema
 * https://schema.org/FAQPage
 * @param {Array<{question: string, answer: string}>} faqs - Array of FAQ items
 */
export const getFAQSchema = (faqs) => ({
  '@context': 'https://schema.org',
  '@type': 'FAQPage',
  mainEntity: faqs.map(faq => ({
    '@type': 'Question',
    name: faq.question,
    acceptedAnswer: {
      '@type': 'Answer',
      text: faq.answer
    }
  }))
});

/**
 * BreadcrumbList Schema
 * https://schema.org/BreadcrumbList
 * @param {Array<{name: string, url: string}>} items - Breadcrumb items
 */
export const getBreadcrumbSchema = (items) => ({
  '@context': 'https://schema.org',
  '@type': 'BreadcrumbList',
  itemListElement: items.map((item, index) => ({
    '@type': 'ListItem',
    position: index + 1,
    name: item.name,
    item: item.url
  }))
});

/**
 * WebPage Schema
 * https://schema.org/WebPage
 * @param {Object} options - Page options
 */
export const getWebPageSchema = ({ name, description, url }) => ({
  '@context': 'https://schema.org',
  '@type': 'WebPage',
  name,
  description,
  url,
  isPartOf: {
    '@type': 'WebSite',
    name: 'Go Ops',
    url: BASE_URL
  },
  publisher: {
    '@type': 'Organization',
    name: 'Go Ops',
    logo: {
      '@type': 'ImageObject',
      url: LOGO_URL
    }
  }
});

/**
 * Default FAQ content for Go Ops
 */
export const defaultFAQs = [
  {
    question: 'What is Go Ops?',
    answer: 'Go Ops is an AI-powered garage management system designed for UK independent garages. It helps garage owners manage jobs, customers, invoices, vehicle health checks, and payments from one platform.'
  },
  {
    question: 'What does Go Ops do for UK garages?',
    answer: 'Go Ops automates booking intake from emails, coordinates technicians via a visual job board, enables digital vehicle health checks with photo/video uploads, handles customer payments through secure links, and generates invoices automatically.'
  },
  {
    question: 'How does Go Ops handle job management?',
    answer: 'Jobs flow through a visual board: incoming bookings are assigned to technicians, who perform digital vehicle health checks. Issues are flagged with red/amber/green status, sent to admin for pricing, and payment links are sent to customers.'
  },
  {
    question: 'Does Go Ops work on mobile?',
    answer: 'Yes, Go Ops is fully responsive and works on mobile devices. Technicians can perform vehicle health checks, upload photos and videos, and manage jobs directly from their phones.'
  },
  {
    question: 'How much does Go Ops cost?',
    answer: 'Contact us for a custom quote based on your garage size and needs. We offer flexible pricing to suit independent garages of all sizes.'
  },
  {
    question: 'How is Go Ops different from GarageHive, AutoLeap, or Garage Manager?',
    answer: 'Go Ops is built with AI at its core—automatically extracting bookings from emails, smart job routing, and integrated vehicle health checks. Unlike traditional garage software, Go Ops reduces manual data entry and streamlines the entire workflow from booking to invoice.'
  },
  {
    question: 'Can I book a Go Ops demo automatically?',
    answer: 'Yes, you can request a demo by emailing hello@go-ops.digital or calling +44 333 2101 500. We offer 30-minute tailored demos that cover the AI booking pipeline, job board, and payment flow.'
  },
  {
    question: 'Does Go Ops have an API?',
    answer: 'Go Ops currently focuses on providing a complete garage management solution through our web and mobile interfaces. For integration inquiries, please contact our team.'
  },
  {
    question: 'What integrations does Go Ops support?',
    answer: 'Go Ops integrates with Book My Garage for booking intake, GSF Car Parts and Euro Car Parts for parts ordering, Stripe for secure payments, and Twilio for SMS notifications.'
  },
  {
    question: 'Is Go Ops GDPR compliant?',
    answer: 'Yes, Go Ops is fully GDPR compliant. We are a UK-based company and handle all customer data in accordance with UK data protection regulations.'
  },
  {
    question: 'What payment methods does Go Ops support?',
    answer: 'Go Ops uses Stripe to process payments, supporting all major credit and debit cards, Apple Pay, and Google Pay. Payment links are sent directly to customers via email and SMS.'
  },
  {
    question: 'How long does onboarding take?',
    answer: 'Most garages are up and running within a week. We provide a tailored onboarding process including data migration, staff training, and ongoing support.'
  }
];

/**
 * ContactPage Schema
 * https://schema.org/ContactPage
 */
export const getContactPageSchema = () => ({
  '@context': 'https://schema.org',
  '@type': 'ContactPage',
  name: 'Contact Go Ops',
  description: 'Book a free 30-minute Go Ops demo. Fill out the form to schedule a tailored walkthrough of AI-powered garage management.',
  url: `${BASE_URL}/contact`,
  mainEntity: {
    '@type': 'Organization',
    name: 'Go Ops',
    telephone: '+44-333-2101-500',
    email: 'hello@go-ops.digital'
  }
});

/**
 * Product Schema for Pricing Page
 * https://schema.org/Product
 */
export const getPricingSchema = () => ({
  '@context': 'https://schema.org',
  '@type': 'Product',
  name: 'Go Ops Garage Management Software',
  description: 'AI-powered garage management system for UK independent garages',
  brand: {
    '@type': 'Organization',
    name: 'Go Ops'
  },
  offers: {
    '@type': 'Offer',
    price: '0',
    priceCurrency: 'GBP',
    priceSpecification: {
      '@type': 'PriceSpecification',
      price: '0',
      priceCurrency: 'GBP',
      valueAddedTaxIncluded: true
    },
    description: 'Contact for custom pricing based on garage size and needs',
    url: `${BASE_URL}/pricing/`,
    availability: 'https://schema.org/InStock',
    seller: {
      '@type': 'Organization',
      name: 'Go Ops'
    }
  }
});

/**
 * Feature Page Schema
 * @param {Object} options - Feature options
 */
export const getFeatureSchema = ({ name, description, url }) => ({
  '@context': 'https://schema.org',
  '@type': 'WebPage',
  name,
  description,
  url,
  about: {
    '@type': 'SoftwareApplication',
    name: 'Go Ops',
    applicationCategory: 'BusinessApplication',
    featureList: [name]
  },
  isPartOf: {
    '@type': 'WebSite',
    name: 'Go Ops',
    url: BASE_URL
  }
});

/**
 * Comparison Page Schema
 * @param {Object} options - Comparison options
 */
export const getComparisonSchema = ({ competitor, description, url }) => ({
  '@context': 'https://schema.org',
  '@type': 'WebPage',
  name: `Go Ops vs ${competitor}`,
  description,
  url,
  about: [
    {
      '@type': 'SoftwareApplication',
      name: 'Go Ops'
    },
    {
      '@type': 'SoftwareApplication',
      name: competitor
    }
  ],
  isPartOf: {
    '@type': 'WebSite',
    name: 'Go Ops',
    url: BASE_URL
  }
});

export default {
  getOrganizationSchema,
  getSoftwareApplicationSchema,
  getFAQSchema,
  getBreadcrumbSchema,
  getWebPageSchema,
  getContactPageSchema,
  getPricingSchema,
  getFeatureSchema,
  getComparisonSchema,
  defaultFAQs
};
